import { OwnerCallToActionCardData, OwnerCallToActionImageProps } from "data/types";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";

import { OWNER_CALL_TO_ACTION_IMAGES } from "data/constants";

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.5, ease: "easeOut" },
  }),
  hover: { scale: 1.03, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)" },
};

// Reusable CallToActionImage Component
function OwnerCallToActionImage({
  title = "Les prestataires événementiels autour de Paris",
  description = "",
  cardData = OWNER_CALL_TO_ACTION_IMAGES,
  className = "bg-gradient-to-br dark:bg-neutral-800 dark:from-neutral-800",
  controls,
}: OwnerCallToActionImageProps) {
  return (
    <div className={className}>
      <div className="container px-4 py-12 mx-auto">
        {/* Animated Title */}
        <div>
          <h2 className="mb-8 text-xl font-bold text-center text-gray-800 md:text-3xl dark:text-white">{title}</h2>
        </div>

        {description && (
          <div className="mb-6 text-sm text-center text-gray-600 dark:text-white">
            <p>{description}</p>
          </div>
        )}
      </div>
      <div className="max-w-6xl pb-12 mx-auto">
        {/* Grid Layout */}
        <div className="space-y-6 ">
          {/* First Row: 3 Images */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
            {cardData.slice(0, 3).map((card, index) => (
              <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                <Card card={card} index={index} />
              </div>
            ))}
          </div>

          {/* Second Row: 3 Images */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-9">
            {cardData.slice(3, 5).map((card, index) => (
              <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                <Card card={card} index={index + 3} />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
            {cardData.slice(5, 8).map((card, index) => (
              <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                <Card card={card} index={index + 3} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-9">
            {cardData.slice(8, 10).map((card, index) => (
              <div key={card.id} className={`lg:col-span-${card.colSpan.lg} md:col-span-${card.colSpan.md}`}>
                <Card card={card} index={index + 3} />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* RTL Support */}
      <style>{`
          [dir='rtl'] .discount-single-item .discount {
            left: auto;
            right: 1.25rem;
          }
        `}</style>
    </div>
  );
}

// Reusable Card Component
function Card({
  card,
  index,
  className = "",
}: {
  card: OwnerCallToActionCardData;
  index: number;
  className?: string;
}) {
  return (
    <AnimatePresence>
      <motion.div
        className={`relative block rounded-2xl overflow-hidden ${className}`}
        style={{ height: card.height }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        custom={index}
      >
        <Link to={card.to} className="block w-full h-full">
          {/* Background Image */}
          <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${card.image})` }} />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(17,24,39,0.9)] to-[rgba(17,24,39,0)]" />
          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <motion.h3
              className="text-lg font-semibold text-white md:text-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              {card.title}
            </motion.h3>
          </div>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}

export default OwnerCallToActionImage;
